<script>
  import Section from '../components/Section.svelte';
  import Panel from '../components/Panel.svelte';
  import Button from '../components/Button.svelte';
  import Async from '../components/Async.svelte';
  import QueryPanel from '../components/QueryPanel.svelte';
  import HelpDrawer from '../components/HelpDrawer.svelte';
  import { runAnalysis } from '../lib/api.js';
  import { HELP } from '../lib/help.js';

  let runId = $state(0);
  // Starts a background job on the backend and polls it to completion. The
  // job loads queries.json from the Pod and runs every entry there, under the
  // institutional credential — the panels below are composed from whatever the
  // Pod holds, not from code.
  const analysis = $derived.by(() => { runId; return runAnalysis(); });

  // Group stored queries by theme, in first-seen order; entries without a theme
  // fall into an "other" group. No query ids or names appear in this file.
  function byTheme(queries) {
    const order = [];
    const groups = new Map();
    for (const q of queries) {
      const theme = q.theme || 'other';
      if (!groups.has(theme)) { groups.set(theme, []); order.push(theme); }
      groups.get(theme).push(q);
    }
    return order.map((theme) => ({ theme, queries: groups.get(theme) }));
  }
</script>

<HelpDrawer {...HELP.analysis} />

<h1 class="wf2-title wf2-title-level-1">Comparisons</h1>
<p class="wf2-subtitle">Read each institution's figures side by side — one panel per saved query, one column per institution</p>

<Section title="Run">
  <Panel>
    <p class="wf2-text">
      Each panel below is one query you saved. Run them again to pick up a
      quarter an institution has shared since you last looked, or write a new one
      over in <a href="#/queries">Queries</a> — it turns up here as soon as you
      save it.
    </p>
    <Button variant="primary" onclick={() => (runId += 1)}>Run again</Button>
  </Panel>
</Section>

<Async promise={analysis} loading="Reading the shared figures…">
  {#snippet children(a)}
    <p class="wf2-caption">
      Ran {a.queries.length} saved quer{a.queries.length === 1 ? 'y' : 'ies'} using
      {a.counts.ownFiles} of your own shared cubes and
      {a.counts.partnerFiles} that {a.partner.label} shares with you.
    </p>

    {#if !a.queries.length}
      <Section title="Nothing saved yet">
        <Panel>
          <p class="wf2-text">
            You haven't saved any queries yet. Build one over in
            <a href="#/queries">Queries</a> and save it — it will appear here as
            a panel.
          </p>
        </Panel>
      </Section>
    {/if}

    {#each byTheme(a.queries) as { theme, queries }}
      <Section title={theme}>
        {#each queries as q}
          <QueryPanel
            query={q}
            result={a.results[q.id] ?? { error: 'No result returned for this query.' }}
            publishers={a.publishers ?? []}
          />
        {/each}
      </Section>
    {/each}

    <div class="wf2-row" style="gap:12px">
      <Button variant="secondary" to="catalog">Catalog</Button>
      <Button variant="secondary" to="queries">Queries</Button>
    </div>
  {/snippet}
</Async>
